import { Chip, ScrollShadow, Tooltip } from '@heroui/react'
import { useStatsigClient } from '@statsig/react-bindings'
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'
import DownloadButton from 'components/Dashboard/CallLogs/DownloadButton'
import PlayButton from 'components/Dashboard/CallLogs/PlayButton'
import CopyButton from 'components/Hero/CopyButton'
import { Button } from 'components/ui/Button'
import { ProgressBar } from 'components/ui/ProgressBar'
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { ArrowDownLeft, ArrowUpRight, ChevronRight, PhoneCall } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { convertToMinutesAndSeconds } from 'utils/formatting/secsToMins'
import { toDtmy } from 'utils/formatting/toDtmy'
import { parsePathwayTags } from 'utils/funcs/call-logs/parsePathwayTags'
import { cn } from '@/lib/utils'

const colorMapWarmTransfer = {
  WAITING: 'default',
  ATTEMPTING: 'default',
  QUEUED: 'default',
  STARTED: 'default',
  MERGED: 'success',
  FAILED: 'danger',
  HUNG_UP: 'warning',
  CANCELLED: 'warning',
  TIMED_OUT: 'danger',
  NO_ANSWER: 'default',
}

const STATUS_COLOR_MAP = {
  'completed': { bg: 'bg-lime-500', text: 'text-lime-700', border: 'border-lime-200 bg-lime-50' },
  'no-answer': { bg: 'bg-gray-500', text: 'text-gray-700', border: 'border-gray-200 bg-gray-50' },
  'unknown': { bg: 'bg-gray-500', text: 'text-gray-700', border: 'border-gray-200 bg-gray-50' },
  'busy': { bg: 'bg-indigo-500', text: 'text-indigo-700', border: 'border-indigo-200 bg-indigo-50' },
  'failed': { bg: 'bg-orange-500', text: 'text-orange-700', border: 'border-orange-200 bg-orange-50' },
  'canceled': { bg: 'bg-blue-400', text: 'text-blue-700', border: 'border-blue-200 bg-blue-50' },
}

function renderPhoneNumber(value) {
  if (!value || typeof value !== 'string')
    return value ?? '-'
  // If it's a web client uuid
  if (value.length === 36) {
    return (
      <Chip size="sm" color="primary" variant="flat" radius="sm">
        Web Client
      </Chip>
    )
  }
  // If it's a phone number
  if (isValidPhoneNumber(value)) {
    const parsedNumber = parsePhoneNumberFromString(value)
    if (parsedNumber) {
      return parsedNumber.formatNational()
    }
  }
  // If it's an inbound label
  if (value?.length) {
    return (
      <Chip size="sm" color="default" variant="flat" radius="sm">
        {value}
      </Chip>
    )
  }
  return '-'
}

export default function useCallLogsTable({ data = [], include = null, onRowClick = null }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { client: statsigClient } = useStatsigClient()

  const { callId } = useParams()
  const rowSelection = useMemo(() => callId ? { [callId]: true } : {}, [callId])

  const sortBy = searchParams.get('sortBy') || 'created_at'
  const sortDir = searchParams.get('sortDir') || 'desc'
  const sorting = useMemo(() => [{ id: sortBy, desc: sortDir === 'desc' }], [sortBy, sortDir])

  const hidePathways = statsigClient.checkGate('hide_pathway_data')

  const handleSortChange = (updater) => {
    const newSorting = updater(sorting)
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0]
      searchParams.set('sortBy', id)
      searchParams.set('sortDir', desc ? 'desc' : 'asc')
    }
    else {
      searchParams.delete('sortBy')
      searchParams.delete('sortDir')
    }
    setSearchParams(searchParams)
  }

  const handleRowSelectionChange = useCallback((updater) => {
    const queryString = searchParams.toString()
    let c_id = updater

    if (typeof updater === 'function') {
      const newSelection = updater()
      c_id = Object.keys(newSelection)[0]
    }

    if (onRowClick) {
      onRowClick(c_id)
      return
    }

    const route = location.pathname.split('/').slice(0, 3).join('/')

    navigate(`${route}/${c_id || ''}${queryString ? `?${queryString}` : ''}`)
  }, [searchParams])

  const columns = useMemo(() => [
    {
      accessorKey: 'recording_url',
      header: 'Recording',
      enableSorting: false,
      size: 20,
      cell: ({ getValue, row }) => {
        const url = getValue()
        return url
          ? (
              <div className="h-full w-10">
                <div className={`
                  absolute top-1/2 w-full -translate-y-1/2 transform
                `}
                >
                  <PlayButton handleSelect={() => handleRowSelectionChange(row.original.c_id)} />
                  <DownloadButton c_id={row.original.c_id} />
                </div>
              </div>
            )
          : '-'
      },
    },
    {
      accessorKey: 'c_id',
      header: 'Call ID',
      size: 20,
      enableSorting: false,
      cell: ({ getValue }) => {
        const callId = getValue()
        // this means that the call hasn't started and that it's queued (for proxy agents.)
        if (callId?.startsWith('temp_'))
          return '-'
        return (
          <div className="h-full w-28">
            <div className="absolute top-1/2 w-full -translate-y-1/2 transform">
              <CopyButton
                value={callId}
                displayText={`${callId?.substring(0, 6)}...`}
                className="text-sm font-normal text-gray-500"
              />
            </div>
          </div>
        )
      },
    },
    {
      id: 'icon',
      header: `In/Out`,
      size: 20,
      enableResizing: false,
      enableSorting: false,
      cell: ({ row }) => {
        const handleExpand = (e) => {
          e.stopPropagation()
          e.preventDefault()
          row.getToggleExpandedHandler()()
        }
        return row.original.transferred_calls?.length
          ? (
              <Tooltip
                content={row.getIsExpanded() ? 'Hide Transferred Call(s)' : 'Show Transferred Call(s)'}
                className="rounded-sm"
                classNames={{
                  base: 'mb-4',
                }}
              >
                <div className="h-full w-28">
                  <div className={`
                    absolute top-1/2 w-full -translate-y-1/2 transform
                  `}
                  >
                    <Button
                      isIconOnly
                      variant="secondary"
                      onClick={handleExpand}
                    >
                      <ChevronRight
                        size={20}
                        color="black"
                        className={cn('mx-auto transition-transform', row.getIsExpanded()
                          ? `rotate-90`
                          : `animate-pulse`)}
                      />
                    </Button>
                  </div>
                </div>
              </Tooltip>
            )
          : row.original.inbound
            ? (
                <Tooltip content="Inbound call" className="rounded-sm">
                  <ArrowDownLeft className="h-5 w-5 text-gray-400" />
                </Tooltip>
              )
            : (
                <Tooltip content="Outbound call" className="rounded-sm">
                  <ArrowUpRight className="h-5 w-5 text-gray-900" />
                </Tooltip>
              )
      },
    },
    {
      accessorKey: 'to_label',
      header: 'To',
      enableSorting: false,
      size: 150,
      cell: ({ getValue }) => {
        const value = getValue()
        return renderPhoneNumber(value)
      },
    },
    {
      accessorKey: 'from_label',
      header: 'From',
      enableSorting: false,
      size: 150,
      cell: ({ getValue }) => {
        const value = getValue()
        return renderPhoneNumber(value)
      },
    },
    {
      accessorKey: 'call_length',
      header: 'Duration',
      size: 20,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue() // seconds
        const minutesAndSeconds = value ? convertToMinutesAndSeconds(value) : ''

        // This is just some arbitrary number to determine at what point the progress bar should be full
        const CALL_LENGTH_FOR_FULL_PROGRESS_BAR = 6 // 6 minutes

        const pct = ((value || 0) / CALL_LENGTH_FOR_FULL_PROGRESS_BAR) * 100
        const val = Math.max(0, Math.min(100, Math.round(pct)))

        return (
          <div className="flex items-center gap-3 truncate text-gray-900">
            <ProgressBar
              value={val}
              isIndeterminate={value === undefined || value === null}
              className="w-11"
            />
            {minutesAndSeconds}
          </div>
        )
      },
    },
    {
      accessorKey: 'issues',
      header: 'Issues',
      enableSorting: false,
      size: 20,
      cell: ({ getValue }) => {
        const issues = getValue()

        const mediumCount = issues?.medium ?? 0
        const highCount = issues?.high ?? 0

        if (!mediumCount && !highCount) {
          return '-'
        }

        return (
          <div className="flex items-center gap-1">
            {mediumCount > 0 && (
              <span className={`
                flex h-4 w-4 items-center justify-center rounded-sm
                bg-yellow-200 text-xs font-medium text-yellow-800
              `}
              >
                {mediumCount}
              </span>
            )}
            {highCount > 0 && (
              <span className={`
                flex h-4 w-4 items-center justify-center rounded-sm
                bg-orange-200 text-xs font-medium text-orange-800
              `}
              >
                {highCount}
              </span>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'created_at',
      enableSorting: true,
      size: 20,
      header: 'Created',
      cell: ({ getValue }) => {
        const date = getValue()
        return date ? toDtmy(new Date(date)) : '-'
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ getValue }) => {
        const status = getValue()
        return <StatusCell status={status || 'unknown'} />
      },
    },
    {
      accessorKey: 'pathway_id',
      header: 'Pathway',
      enableSorting: false,
      size: 50,
      cell: ({ getValue, row }) => {
        const pathwayId = getValue()
        return pathwayId
          ? (
              <Link
                to={`/dashboard/convo-pathways?id=${pathwayId}`}
                className={`
                  max-w-20 truncate text-ellipsis underline
                  hover:text-blue-600
                `}
                isDisabled={hidePathways}
                onClick={e => e.stopPropagation()}
              >
                {row.original.pathway_name}
              </Link>
            )
          : '-'
      },
    },
    {
      accessorKey: 'pathway_tags',
      header: 'Tags',
      enableSorting: false,
      cell: ({ getValue }) => {
        const tags = getValue()
        const parsedTags = parsePathwayTags(tags)
        if (!parsedTags || !parsedTags?.length)
          return '-'

        return (
          <ScrollShadow
            orientation="horizontal"
            className="flex max-w-60 gap-1 px-2"
            hideScrollBar
          >
            {parsedTags?.map(tag => (
              <Chip size="sm" color="default" variant="bordered" radius="sm" key={tag.name ?? tag}>
                <div className="flex items-center gap-1">
                  <div className="size-2 rounded-full" style={{ backgroundColor: tag.color ?? 'gray' }} />
                  {tag.name ?? tag}
                </div>
              </Chip>
            ))}
          </ScrollShadow>
        )
      },
    },
    {
      accessorKey: 'transferred_to',
      header: 'Transferred To',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        // if this is a normal call, where there are no warm transfers
        const isWarmTransferCall = row.original.transferred_calls?.length
        const isProxyAgentCall = row.original.is_proxy_agent_call
        const isUnfinishedProxyAgentCall = row.original.c_id.startsWith('temp_')

        if (!isWarmTransferCall && !isProxyAgentCall && !isUnfinishedProxyAgentCall) {
          const value = getValue()
          return renderPhoneNumber(value)
        }

        if (isWarmTransferCall) {
          const value = getValue()
          if (value && value !== 'HOLD MUSIC') {
            return renderPhoneNumber(value)
          }
          const state = row.original.warm_transfer_call?.state?.split('_').join(' ')
          return (
            <Tooltip content={state}>
              <Chip size="sm" color={colorMapWarmTransfer[state] ?? 'default'} variant="dot" radius="sm">
                {state}
              </Chip>
            </Tooltip>
          )
        }

        if (isUnfinishedProxyAgentCall) {
          return (
            <Chip size="sm" color={colorMapWarmTransfer.WAITING} variant="dot" radius="sm">
              WAITING
            </Chip>
          )
        }

        const call_status = row.original.state?.split('_').join(' ')
        return (
          <Chip size="sm" color={colorMapWarmTransfer[call_status] ?? 'default'} variant="dot" radius="sm">
            {call_status}
          </Chip>
        )
      },
    },
    {
      accessorKey: 'batch_id',
      header: 'Batch ID',
      enableSorting: false,
      cell: ({ getValue }) => {
        const batchId = getValue()
        if (!batchId)
          return '-'
        return (
          <div className="h-full w-28">
            <div className="absolute top-1/2 w-full -translate-y-1/2 transform">
              <CopyButton
                value={batchId}
                displayText={`${batchId.substring(0, 6)}...`}
                className="text-sm font-normal text-gray-500"
              />
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'review_status',
      header: 'Review Status',
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.original.metadata?.qc_status
        return status ? <QAStatusCell status={status} /> : '-'
      },
    },
    {
      accessorKey: 'error_message',
      header: '',
      enableSorting: false,
      size: 20,
      cell: ({ getValue, row }) => {
        const error = getValue()

        return (
          <div className="flex items-center gap-2">
            {row.original?.status === 'completed' && (
              <PhoneCall className="h-4.5 w-4.5 text-gray-500" strokeWidth={1.8} />
            )}
            {!!error && (
              <div className={`
                absolute top-0 right-0 h-full w-1.5 rounded-e-md bg-red-600
              `}
              />
            )}
          </div>
        )
      },
    },
  ], [])

  const filteredColumns = useMemo(() => {
    if (include) {
      return columns.filter(column => column.id === 'icon' || include.includes(column.accessorKey))
    }
    return columns
  }, [include, columns])

  return useReactTable({
    data,
    columns: filteredColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    // EXPANSION
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: row => !!row.original.transferred_calls,
    getSubRows: row => row.transferred_calls?.length ? row.transferred_calls : [],
    // RESIZING
    columnResizeMode: 'onChange',
    // SORTING
    manualSorting: true,
    sortDescFirst: true,
    onSortingChange: handleSortChange,
    // SELECTION
    getRowId: row => row.c_id,
    enableMultiRowSelection: false,
    enableSubRowSelection: false,
    enableRowSelection: true,
    onRowSelectionChange: handleRowSelectionChange,
  })
}

function StatusCell({ status }) {
  const color = STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.unknown
  return (
    <div className="h-full w-20">
      <div className="absolute top-1/2 w-full -translate-y-1/2 transform">
        <div className={cn(`
          flex w-fit items-center gap-2 rounded-sm border py-0.5 pr-1.5 pl-2
        `, color.border)}
        >
          <div className={cn('h-1.5 w-1.5 rounded-full', color.bg)} />
          <div className={cn('text-xs capitalize', color.text)}>{status?.split('-')?.join(' ')}</div>
        </div>
      </div>
    </div>
  )
}

const statusMap = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  not_needed: 'Not Needed',
}
function QAStatusCell({ status }) {
  const color = STATUS_COLOR_MAP[status] || STATUS_COLOR_MAP.unknown
  return (
    <div className="h-full w-20">
      <div className="absolute top-1/2 w-full -translate-y-1/2 transform">
        <div className={cn(`
          flex w-fit items-center gap-2 rounded-sm border py-0.5 pr-1.5 pl-2
        `, color.border)}
        >
          <div className={cn('h-1.5 w-1.5 rounded-full', color.bg)} />
          <div className={cn('text-xs capitalize', color.text)}>{statusMap[status] || status?.split('-')?.join(' ')}</div>
        </div>
      </div>
    </div>
  )
}
